import clsx from 'clsx'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import AddMaintenancePointModal from 'pages/Assets/maintenance/AddMaintenancePointModal/AddMaintenancePointModal'
import MaintenancePointItem from 'pages/Assets/maintenance/MaintenancePointItem/MaintenancePointItem'
import { KeyboardEvent, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'
import { IMaintenancePoint } from 'utils/interfaces'

import tableStyle from './AssetsMaintenanceTable.module.css'

export type AssetsMaintenancePanelProps = {
  facilityId: number
  points: IMaintenancePoint[]
  loadingProgress?: PROGRESS_TYPES
  highlightedPointId?: number
  onPointsUpdated: () => void
  onPointClicked?: (id?: number) => void
  panelPage?: number
  panelPageSize?: number
  panelPageInput?: string
  setPanelPage?: React.Dispatch<React.SetStateAction<number>>
  setPanelPageInput?: (val: string) => void
  onPanelPageJump?: (e: KeyboardEvent<HTMLInputElement>) => void
  isActive?: boolean
  hideDelete?: boolean
}

const AssetsMaintenancePanel = ({
  facilityId,
  points,
  loadingProgress = PROGRESS_TYPES.SUCCESS,
  highlightedPointId,
  onPointsUpdated,
  onPointClicked,
  panelPage = 1,
  panelPageSize = 20,
  panelPageInput = '1',
  setPanelPage,
  setPanelPageInput,
  onPanelPageJump,
  isActive = true,
  hideDelete,
}: AssetsMaintenancePanelProps) => {
  const { t } = useTranslation()

  const visiblePoints = useMemo(
    () => points.slice((panelPage - 1) * panelPageSize, panelPage * panelPageSize),
    [panelPage, panelPageSize, points]
  )

  if (loadingProgress === PROGRESS_TYPES.WORK) {
    return <Loader />
  }

  if (loadingProgress === PROGRESS_TYPES.ERROR) {
    return (
      <div className={tableStyle.emptyWrapper}>
        <div className={tableStyle.emptyTitle}>{t('common.error')}</div>
      </div>
    )
  }

  return (
    <div className={tableStyle.tableWrapper}>
      <div className={tableStyle.tableScrollArea}>
        <div className={tableStyle.tableScrollContent}>
          <div className={tableStyle.tableHeader}>
            <div />
            <div className={clsx(tableStyle.tableHeaderItem, 'text-16-400')}>Name</div>
            <div className={clsx(tableStyle.tableHeaderItem, tableStyle.actionsHeader, 'text-16-400')}>
              Available actions
            </div>
            <div />
          </div>
          {points.length ? (
            <div className={tableStyle.table}>
              {visiblePoints.map(point => (
                <div key={point.id} onClick={() => onPointClicked?.(point.id)}>
                  <MaintenancePointItem
                    point={point}
                    points={points}
                    facilityId={facilityId}
                    highlighted={point.id === highlightedPointId}
                    onPointUpdated={onPointsUpdated}
                    hideDelete={hideDelete}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={tableStyle.emptyWrapper}>
              <div className={tableStyle.emptyTitle}>No maintenance points</div>
              <div className={tableStyle.emptyDescription}>Add a maintenance point for this facility.</div>
            </div>
          )}
        </div>
      </div>
      {points.length > panelPageSize && setPanelPage && setPanelPageInput && (
        <div className={tableStyle.paginationFooter}>
          <TablePagination
            page={panelPage}
            totalPages={Math.ceil(points.length / panelPageSize) || 1}
            pageInput={panelPageInput}
            onPageInputChange={setPanelPageInput}
            onPageInputKeyDown={onPanelPageJump ?? (() => {})}
            onPageInputBlur={() => setPanelPageInput(panelPage.toString())}
            onPrev={() => setPanelPage(prev => Math.max(prev - 1, 1))}
            onNext={() => setPanelPage(prev => prev + 1)}
          />
        </div>
      )}
    </div>
  )
}

export default AssetsMaintenancePanel

export { AddMaintenancePointModal }
